/* Tisk vyplněného hypotečního dotazníku → PDF pro banku.
 *
 * PROČ TENHLE NÁSTROJ A NE jsPDF:
 * Repo má dvě cesty k PDF. jsPDF + html2canvas (lib/pdfGenerator.js) vyrábí
 * RASTR — obrázek uvnitř PDF. To je fajn na marketingovou modelaci s grafy,
 * ale mizerné pro dokument do banky: text se nedá vyhledat ani zkopírovat,
 * bankovní archiv ho musí OCRovat a diakritika je závislá na fontu v canvasu.
 * Druhá cesta je tisk prohlížečem (window.open + @media print), kterou už v
 * repu používá výpověď smlouvy (lib/cancellationLetter.js) i tiskové stránky
 * kalkulaček. Text zůstane vektorový a vyhledatelný, stránkování řeší
 * prohlížeč a hlavička je tatáž brandovaná jako u ostatních výstupů
 * (lib/printBrand.js). Proto tisk. Nový PDF engine se nepíše.
 *
 * DVA REŽIMY:
 *  - `plne: true`  — dokument pro banku, s rodným číslem. Vědomá akce.
 *  - `plne: false` — interní kopie, citlivé údaje maskované.
 * Rozdíl je vidět i v hlavičce dokumentu, aby se ty dva papíry nedaly splést.
 */
import { printBrandHeader, printBrandFooter, brandColorOf } from "@/lib/printBrand";
import { jeVyplneno, maskujCitlive, formatujCastku } from "./stavDotazniku";
import { relevantniPoleSekce, sekceRelevantni } from "@/lib/hypoteka/podminky";

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

/* Nevyplněné pole se do dokumentu pro banku píše jako pomlčka, NIKDY jako
   nula ani prázdno. Banka musí poznat rozdíl mezi „klient nemá žádné závazky“
   a „na tuhle otázku klient neodpověděl“ — to první je tvrzení, to druhé díra. */
const PRAZDNO = "—";

/* Brand barva poradce jde do <style> bloku, kde by escapování HTML nepomohlo:
   řetězec typu </style><script> by ze stylu utekl. Proto propustíme jen tvar,
   který barva opravdu má; cokoliv jiného spadne na výchozí odstín. */
const BARVA_OK = /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]{3,20})$/;
const barvaBezpecne = (c) => (BARVA_OK.test(String(c || "")) ? String(c) : "#F47920");

/* printBrandHeader/printBrandFooter vkládají brand_color do stylů BEZ ošetření
   (lib/printBrand.js). Ten soubor sdílí půlka kalkulaček, takže ho tady měnit
   nebudeme — místo toho mu podstrčíme profil s barvou už prověřenou. */
const poradceBezpecne = (advisor) => (advisor
  ? { ...advisor, brand_color: barvaBezpecne(advisor.brand_color) }
  : advisor);

/** Jedna hodnota → text do dokumentu (s maskováním, když nejde o plný export). */
function hodnotaText(pole, odpovedi, plne, lang) {
  const raw = odpovedi ? odpovedi[pole.id] : undefined;
  if (!jeVyplneno(raw)) return PRAZDNO;
  if (pole.citlive && !plne) return maskujCitlive(pole.id, raw) || PRAZDNO;
  return formatujCastku(raw, pole.typ, lang) || String(raw);
}

/** Tabulkové pole → HTML tabulka. Prázdná tabulka se nekreslí vůbec. */
function tabulkaHtml(pole, odpovedi, lang) {
  const radky = Array.isArray(odpovedi?.[pole.id]) ? odpovedi[pole.id].filter(Boolean) : [];
  const sloupce = Array.isArray(pole.sloupce) ? pole.sloupce : [];
  if (!radky.length || !sloupce.length) return "";
  const hlavicka = sloupce.map((s) => `<th>${esc(s.label || s.id)}</th>`).join("");
  const telo = radky
    .map((r) => `<tr>${sloupce.map((s) => `<td>${jeVyplneno(r?.[s.id]) ? esc(formatujCastku(r[s.id], s.typ, lang) || r[s.id]) : PRAZDNO}</td>`).join("")}</tr>`)
    .join("");
  return `<div class="tab-nazev">${esc(pole.label)}</div>
    <table class="tab"><thead><tr>${hlavicka}</tr></thead><tbody>${telo}</tbody></table>`;
}

/** Jedna sekce dotazníku. */
function sekceHtml(sekce, odpovedi, plne, bc, lang) {
  /* Do dokumentu jde jen to, na co se dotazník tohohle klienta opravdu ptal.
     Sekce Spolužadatel u sólo žadatele by jinak vytiskla šestnáct řádků se
     samými pomlčkami a banka by četla „na tohle klient neodpověděl“ tam, kde
     se ho nikdo neptal. */
  const pole = relevantniPoleSekce(sekce, odpovedi);
  const skalary = pole.filter((p) => p && p.typ !== "tabulka");
  const tabulky = pole.filter((p) => p && p.typ === "tabulka");

  const radky = skalary
    .map((p) => {
      const zamek = p.citlive && !plne ? ' <span class="zamek">skryto</span>' : "";
      return `<tr><td class="lbl">${esc(p.label)}${zamek}</td><td class="val">${esc(hodnotaText(p, odpovedi, plne, lang))}</td></tr>`;
    })
    .join("");

  return `<section class="sekce">
      <h2 style="color:${bc}">${esc(sekce?.nazev || "")}</h2>
      ${radky ? `<table class="pary">${radky}</table>` : ""}
      ${tabulky.map((t) => tabulkaHtml(t, odpovedi, lang)).join("")}
    </section>`;
}

/**
 * Otevře tiskové okno s vyplněným dotazníkem.
 *
 * @param {object} o
 * @param {Array}  o.sekce     definice sekcí ze serveru (nikdy se nedrží na frontendu)
 * @param {object} o.odpovedi  odpovědi klienta
 * @param {string} o.klient    jméno klienta do hlavičky dokumentu
 * @param {object} o.advisor   profil poradce pro brandovanou hlavičku (může být null)
 * @param {boolean} o.plne     true = dokument pro banku s rodným číslem
 * @param {string} o.lang      "cs" | "sk"
 * @param {Function} o.otevri  injektovatelný window.open (kvůli testům)
 * @throws {Error} "popup-blocked" když prohlížeč okno nepustí
 */
export function otevriTiskDotazniku({
  sekce = [],
  odpovedi = {},
  klient = "",
  advisor = null,
  plne = false,
  lang = "cs",
  otevri,
} = {}) {
  const poradce = poradceBezpecne(advisor);
  const bc = barvaBezpecne(brandColorOf(poradce));
  const L = (sk, cs) => (lang === "sk" ? sk : cs);
  const nadpis = L("Dotazník k hypotéke", "Dotazník k hypotéce");
  const seznam = (Array.isArray(sekce) ? sekce.filter(Boolean) : [])
    .filter((s) => sekceRelevantni(s, odpovedi));

  /* Varovný pruh je součást dokumentu, ne jen dialogu před tiskem. Vytištěný
     papír s rodným číslem se povaluje po stolech dýl než dialog na obrazovce. */
  const pruh = plne
    ? `<div class="varovani">${esc(L(
        "Dokument obsahuje rodné číslo a čísla dokladov. Odovzdávajte ho len banke, neposielajte ho nezabezpečeným e-mailom.",
        "Dokument obsahuje rodné číslo a čísla dokladů. Předávejte ho jen bance, neposílejte nezabezpečeným e-mailem.",
      ))}</div>`
    : `<div class="interni">${esc(L(
        "Interná kópia — citlivé údaje sú skryté. Pre banku vytlačte plnú verziu.",
        "Interní kopie — citlivé údaje jsou skryté. Pro banku vytiskněte plnou verzi.",
      ))}</div>`;

  const html = `<!DOCTYPE html><html lang="${lang === "sk" ? "sk" : "cs"}"><head>
      <meta charset="UTF-8" />
      <title>${esc(nadpis)} — ${esc(klient)}</title>
      <style>
        @page { size: A4 portrait; margin: 12mm; }
        /* Dokument je papír, ne obrazovka. Bez color-scheme: light ho prohlížeč
           v tmavém režimu vykreslí tmavým textem na tmavém pozadí a poradce si
           před tiskem nepřečte vlastní výstup (živá kontrola 11. 8. 2026).
           print-color-adjust drží barevný varovný pruh i na papíře. */
        :root { color-scheme: light; }
        html, body { background: #ffffff; }
        body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color: #1d1b16; max-width: 780px; margin: 0 auto; padding: 24px; font-size: 12px; line-height: 1.45;
               -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        h1 { font-size: 20px; margin: 14px 0 2px; }
        .sub { color: #8a8275; font-size: 11px; margin-bottom: 14px; }
        .varovani { background: #fdecea; border: 1px solid #e0b4ad; color: #9c2b17; border-radius: 8px; padding: 8px 11px; font-size: 11px; font-weight: 600; margin: 10px 0 4px; }
        .interni { background: #f2efe6; border: 1px solid #ded7c6; color: #6b6355; border-radius: 8px; padding: 8px 11px; font-size: 11px; margin: 10px 0 4px; }
        section.sekce { margin-top: 16px; page-break-inside: avoid; }
        h2 { font-size: 14px; margin: 0 0 5px; padding-bottom: 3px; border-bottom: 1px solid #e8e2d5; }
        table { width: 100%; border-collapse: collapse; }
        table.pary td { padding: 3px 0; vertical-align: top; border-bottom: 1px solid #f4efe5; }
        td.lbl { color: #6b6355; width: 52%; padding-right: 10px; }
        td.val { font-weight: 600; }
        .zamek { color: #9c2b17; font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em; }
        .tab-nazev { margin-top: 8px; font-size: 11px; color: #6b6355; font-weight: 700; }
        table.tab { margin-top: 3px; font-size: 11px; }
        table.tab th { text-align: left; font-size: 9px; text-transform: uppercase; color: #8a8275; border-bottom: 1px solid #e8e2d5; padding: 3px 6px 3px 0; }
        table.tab td { padding: 3px 6px 3px 0; border-bottom: 1px solid #f4efe5; }
        .akce { margin: 0 0 14px; }
        .akce button { padding: 8px 15px; margin-right: 8px; background: ${bc}; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 12px; }
        @media print { .akce { display: none; } body { padding: 0; } }
      </style>
    </head><body>
      <div class="akce">
        <button onclick="window.print()">${esc(L("Vytlačiť / uložiť PDF", "Vytisknout / uložit PDF"))}</button>
      </div>
      ${printBrandHeader({ title: nadpis, advisor: poradce, locale: lang === "sk" ? "sk-SK" : "cs-CZ" })}
      <h1>${esc(nadpis)}</h1>
      <div class="sub">${esc(klient || L("Klient neuvedený", "Klient neuveden"))}</div>
      ${pruh}
      ${seznam.map((s) => sekceHtml(s, odpovedi, plne, bc, lang)).join("")}
      <div class="sub" style="margin-top:16px">${esc(L(
        "Pomlčka znamená, že klient pole nevyplnil. Nie je to nula.",
        "Pomlčka znamená, že klient pole nevyplnil. Není to nula.",
      ))}</div>
      ${printBrandFooter({ advisor: poradce, lang })}
    </body></html>`;

  const open = otevri || (typeof window !== "undefined" ? window.open.bind(window) : null);
  const w = open ? open("", "_blank") : null;
  if (!w) throw new Error("popup-blocked");
  w.document.write(html);
  w.document.close();
  if (w.focus) w.focus();
  return w;
}
